import React, { useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, Paper, Typography, IconButton } from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { API_BASE } from '../../config/constants';
import { ObjectModel, useObjectsTab, fetchObjectReturns } from './hooks/HObjects';
import { useDocs } from './hooks/HDocs';
import { useWatch } from './hooks/HWatch';
import { useCompanies } from './hooks/HCompanies';
import { User } from './interfaces/IUsers';
import { StarIcon, MailIcon } from '../utils/IconSet';
import { useTableData } from './hooks/HData';
import YieldComparisonChart from './charts/YieldCharts';
import { compoundReturns, annualizedReturn, annualizedVolatility, sharpeRatio } from '../../utils/helpers';

const ObjectMessageForm = React.lazy(() => import('../dialogs/ObjectMessageForm'));

interface Doc {
    id?: string | number;
    document_id?: string | number;
    url: string;
    name: string;
    contentType?: string;
    size?: number;
    lastModified?: string;
}

interface ObjectDetailsProps {
    objectId: string | number | null;
    initialObject?: ObjectModel | null;
    onBack?: () => void;
    user?: User | null;
    // optional watchlist passed from parent (ObjectsTab)
    watchList?: Array<any> | null;
}

const OBJECT_FIELDS: Array<{ key: string; label: string }> = [
    { key: 'name', label: 'Name' },
    { key: 'type_', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'company_name', label: 'Company Name' },
    { key: 'owner', label: 'Owner' },
    { key: 'country', label: 'Country' },
    { key: 'state', label: 'State' },
    { key: 'city', label: 'City' },
    { key: 'location', label: 'Location' },
    { key: 'street', label: 'Street' },
    { key: 'address', label: 'Address' },
    { key: 'zip_code', label: 'Zip Code' },
    { key: 'zoning', label: 'Zoning' },
    { key: 'occupancy', label: 'Occupancy' },
    { key: 'currency', label: 'Currency' },
    { key: 'class', label: 'Class' },
    { key: 'units', label: 'Units' },
    { key: 'Floors', label: 'Floors' },
    { key: 'Parking_Spaces', label: 'Parking Spaces' },
    { key: 'sqm', label: 'SQM' },
    { key: 'GLA', label: 'GLA' },
    { key: 'price', label: 'Price' },
    { key: 'yield_net', label: 'Yield Net' },
    { key: 'yield_gross', label: 'Yield Gross' },
    { key: 'gross_rental_income', label: 'Gross Rental Income' },
    { key: 'operating_expenses', label: 'Operating Expenses' },
    { key: 'NOI', label: 'NOI' },
    { key: 'cap_rate', label: 'Cap Rate' },
    { key: 'yield_dev', label: 'Yield Dev' },
    { key: 'yield_rev', label: 'Yield Rev' },
    { key: 'img_url', label: 'Image URL' },
    { key: 'nda', label: 'NDA' },
];

const RETURN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDisplay = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
};

const formatYearEndTick = (value: unknown) => {
    const label = String(value ?? '');
    if (!label.includes('-12')) {
        return '';
    }

    const [year] = label.split('-');
    return `Dec ${year}`;
};

const getDocVisual = (doc: Doc) => {
    const extension = String(doc.name || '').split('.').pop()?.toLowerCase() || '';
    const contentType = String(doc.contentType || '').toLowerCase();

    const isPdf = extension === 'pdf' || contentType.includes('pdf');
    const isDocx = extension === 'docx' || contentType.includes('wordprocessingml');

    if (isPdf) {
        return {
            label: 'PDF',
            className: 'object-doc-pdf',
        };
    }

    if (isDocx) {
        return {
            label: 'DOCX',
            className: 'object-doc-docx',
        };
    }

    return {
        label: extension ? extension.toUpperCase() : 'FILE',
        className: 'object-doc-file',
    };
};

export default function ObjectDetails({ objectId, initialObject, onBack, user }: ObjectDetailsProps) {
    const { fetchObjectDocs } = useObjectsTab();
    const { openDocument } = useDocs();
    const { data: tableData, loadTableData } = useTableData('data');

    const [objectData, setObjectData] = useState<ObjectModel | null>(initialObject ?? null);
    const [objectLoading, setObjectLoading] = useState(false);
    const [objectError, setObjectError] = useState<string | null>(null);

    const [docs, setDocs] = useState<Doc[]>([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [docsError, setDocsError] = useState<string | null>(null);

    const [returns, setReturns] = useState<Array<{ my_row_id: number; object_id: number; date: string; return: number }>>([]);
    const [returnsLoading, setReturnsLoading] = useState(false);
    const [returnsError, setReturnsError] = useState<string | null>(null);

    const currentUserId = user?.id ?? (user as any)?.user_id ?? null;
    const { watchList: internalWatchList, loadWatchList, createWatch, removeWatch } = useWatch(currentUserId);
    const [isWatched, setIsWatched] = useState<boolean>(false);
    const [togglingWatch, setTogglingWatch] = useState<boolean>(false);

    // Messaging dialog state (dialog moved to a separate component)
    const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

    // detect dark theme (matches CSS: :root.theme-dark or body.theme-dark or .app-layout.dark)
    const isThemeDark = (() => {
        if (typeof window === 'undefined') return false;
        const docEl = document.documentElement;
        const body = document.body;
        if (docEl && docEl.classList && docEl.classList.contains('theme-dark')) return true;
        if (body && body.classList && body.classList.contains('theme-dark')) return true;
        if (document.querySelector && document.querySelector('.app-layout.dark')) return true;
        return false;
    })();

    const envelopeColor = isThemeDark ? '#FFD700' : undefined;

    const { companies } = useCompanies();
    const companyMap = useMemo(() => {
        const m = new Map<string, string>();
        (companies || []).forEach((c: any) => {
            const id = String(c.id ?? c.company_id ?? '');
            const name = String(c.company_name ?? c.company ?? c.name ?? '').trim();
            if (id) m.set(id, name || id);
        });
        return m;
    }, [companies]);

    useEffect(() => {
        console.debug('ObjectDetails: user changed', { user, currentUserId });
    }, [user, currentUserId]);

    useEffect(() => {
        console.debug('ObjectDetails: mounted', { objectId, currentUserId });
    }, [objectId, currentUserId]);

    useEffect(() => {
        setObjectData(initialObject ?? null);
    }, [initialObject]);

    useEffect(() => {
        loadTableData();
    }, [loadTableData]);

    useEffect(() => {
        if (objectId === null || objectId === undefined || String(objectId).trim() === '') {
            return;
        }

        const fetchObject = async () => {
            setObjectLoading(true);
            setObjectError(null);

            try {
                const response = await fetch(`${API_BASE}/api/objects/get-all-objects`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                });

                if (!response.ok) throw new Error('Failed to fetch object data');

                const result = await response.json();
                const allObjects = Array.isArray(result)
                    ? result
                    : Array.isArray(result?.data)
                        ? result.data
                        : [];

                const foundObject = allObjects.find((obj: ObjectModel) => String(obj.id) === String(objectId)) || null;
                setObjectData(foundObject);
                if (!foundObject) {
                    setObjectError('Object not found');
                }
            } catch (err: any) {
                setObjectError(err.message || 'Failed to fetch object data');
            } finally {
                setObjectLoading(false);
            }
        };

        fetchObject();
    }, [objectId]);

    useEffect(() => {
        if (objectId === null || objectId === undefined || String(objectId).trim() === '') {
            setDocs([]);
            return;
        }

        const fetchDocs = async () => {
            setDocsLoading(true);
            setDocsError(null);
            setDocs([]);

            try {
                const loadedDocs = await fetchObjectDocs(objectId);
                setDocs(loadedDocs);
            } catch (err: any) {
                setDocsError(err.message || 'Failed to fetch documents');
                setDocs([]);
            } finally {
                setDocsLoading(false);
            }
        };

        fetchDocs();
    }, [objectId, fetchObjectDocs]);

    useEffect(() => {
        const sourceList = Array.isArray((initialObject as any)?.watchList) ? (initialObject as any).watchList : internalWatchList;
        const idStr = objectId == null ? '' : String(objectId);
        const found = Array.isArray(sourceList) && sourceList.some((w: any) => {
            const candidate = String(w.object_id ?? w.objectId ?? w.id ?? w.object ?? '');
            return candidate === idStr;
        });
        setIsWatched(Boolean(found));
    }, [internalWatchList, initialObject, objectId]);

    useEffect(() => {
        if (objectId === null || objectId === undefined || String(objectId).trim() === '') {
            setReturns([]);
            return;
        }

        const loadReturns = async () => {
            setReturnsLoading(true);
            setReturnsError(null);
            setReturns([]);
            try {
                const result = await fetchObjectReturns(objectId);
                if (result.success) {
                    setReturns(result.data);
                } else {
                    setReturnsError(result.message || 'Failed to load returns');
                }
            } catch (err: any) {
                setReturnsError(err.message || 'Failed to load returns');
            } finally {
                setReturnsLoading(false);
            }
        };

        loadReturns();
    }, [objectId]);

    const visibleFields = useMemo(() => {
        if (!objectData) return [];
        return OBJECT_FIELDS.filter((field) => {
            if (!(field.key in objectData)) return false;
            const value = (objectData as any)[field.key];
            return value !== null && value !== undefined && value !== '';
        });
    }, [objectData]);

    const returnsByYear = useMemo(() => {
        const grouped = new Map<string, Record<number, number>>();

        returns.forEach((row) => {
            const parsedDate = new Date(row.date);
            if (Number.isNaN(parsedDate.getTime())) {
                return;
            }

            const year = String(parsedDate.getUTCFullYear());
            const monthIndex = parsedDate.getUTCMonth();
            const existingYear = grouped.get(year) ?? {};
            existingYear[monthIndex] = row.return;
            grouped.set(year, existingYear);
        });

        return Array.from(grouped.entries())
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([year, months]) => ({ year, months }));
    }, [returns]);

    const monthlyChartData = useMemo(() => {
        return [...returns]
            .filter((row) => row.date)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .map((row) => {
                const parsedDate = new Date(row.date);
                const monthLabel = Number.isNaN(parsedDate.getTime())
                    ? row.date
                    : `${parsedDate.getUTCFullYear()}-${String(parsedDate.getUTCMonth() + 1).padStart(2, '0')}`;

                return {
                    month: monthLabel,
                    return: row.return,
                };
            });
    }, [returns]);

    const cumulativeChartData = useMemo(() => {
        let compounded = 1;

        return monthlyChartData.map((entry) => {
            compounded *= 1 + entry.return / 100;

            return {
                month: entry.month,
                cumulativeReturn: ((compounded - 1) * 100).toFixed(2),
            };
        });
    }, [monthlyChartData]);

    const description = toDisplay(objectData?.description);

    // objectYieldGross removed (moved to YieldCharts component)

    // Yield comparison chart logic moved to `YieldCharts.tsx` and will be rendered below

    const allReturnValues = returns.map((r) => r.return);
    const statsAnnReturn = annualizedReturn(allReturnValues);
    const statsAnnVol = annualizedVolatility(allReturnValues);
    const statsSharpe = sharpeRatio(allReturnValues);
    const totalReturn = allReturnValues.reduce((acc, curr) => acc + curr, 0);

    return (
        <div className="object-details-page object-details-vertical-layout">
            <div className="object-details-content">
                <h1 className="object-details-title">
                    Object Details
                    <button
                        type="button"
                        className="object-watch-toggle"
                        aria-pressed={isWatched}
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (togglingWatch || objectId == null) return;
                            const userId = currentUserId;
                            if (!userId) {
                                console.warn('ObjectDetails: cannot toggle watch — no current user');
                                return;
                            }

                            // optimistic UI update
                            setTogglingWatch(true);
                            setIsWatched((prev) => !prev);

                            try {
                                const ok = isWatched
                                    ? await removeWatch(userId, objectId)
                                    : await createWatch(userId, objectId);

                                if (!ok) {
                                    // revert optimistic change
                                    setIsWatched((prev) => !prev);
                                }

                                await loadWatchList();
                            } catch (err) {
                                console.error('toggle watch error', err);
                                setIsWatched((prev) => !prev);
                            } finally {
                                setTogglingWatch(false);
                            }
                        }}
                        title={isWatched ? 'Remove watch' : 'Add watch'}
                    >
                        <StarIcon
                        className={isWatched ? 'star-icon star-watched' : 'star-icon'}
                        color={isWatched ? '#FFD700' : undefined}
                        fill={isWatched ? '#FFD700' : 'none'}
                        />
                    </button>
                </h1>
                <Button variant="outlined" onClick={onBack}>Back to Objects</Button>
            </div>
            <div className="object-details-id" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div>
                    {objectData?.name
                        ? `${toDisplay(objectData.name)}`
                        : `Object ID: ${toDisplay(objectId)}`}
                </div>
                <IconButton
                    aria-label="send-message"
                    size="small"
                    onClick={() => {
                        if (!currentUserId) {
                            // if not logged in, show a brief console warning and do nothing
                            setMessageDialogOpen(false);
                            return;
                        }
                        setMessageDialogOpen(true);
                    }}
                >
                    <MailIcon color={envelopeColor} />
                </IconButton>
            </div>
            {/* Dialog moved to dedicated component */}
            <React.Suspense fallback={null}>
                {/* Lazy-load to keep ObjectDetails lightweight */}
                <ObjectMessageForm
                    open={messageDialogOpen}
                    onClose={() => setMessageDialogOpen(false)}
                    objectId={objectId}
                    userId={currentUserId}
                />
            </React.Suspense>
            <div className="object-details-papers">
                <Paper className="object-details-paper">
                    <Typography variant="h6" className="object-details-paper-title">Documents</Typography>
                    {docsLoading ? (
                        <div className="object-details-spinner">
                            <CircularProgress />
                        </div>
                    ) : docsError ? (
                        <div className="object-details-error">{docsError}</div>
                    ) : docs.length === 0 ? (
                        <div className="object-details-empty">No documents found for this object.</div>
                    ) : (
                        <div className="object-details-docs-grid">
                            {docs.map((doc, idx) => {
                                const isImage = doc.contentType?.startsWith('image/');
                                const visual = getDocVisual(doc);
                                return (
                                    <div
                                        key={`${doc.url}-${idx}`}
                                        className={`object-details-doc-card ${visual.className}`}
                                        onClick={() => openDocument(doc.url, { fileName: doc.name, contentType: doc.contentType, objectName: objectData?.name })}
                                    >
                                        {isImage ? (
                                            <img
                                                src={doc.url}
                                                alt={doc.name}
                                                className="object-details-doc-image"
                                            />
                                        ) : (
                                            <div className="object-details-doc-preview">
                                                <div className="object-details-doc-badge">
                                                    {visual.label}
                                                </div>
                                            </div>
                                        )}
                                        <div className="object-details-doc-meta">
                                            <strong>{doc.name || 'Unnamed file'}</strong>
                                            {typeof doc.size === 'number' && (
                                                <div className="object-details-doc-size">
                                                    {(doc.size / 1024).toFixed(1)} KB
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Paper>
                <Paper className="object-details-paper">
                    <Typography variant="h6" className="object-details-paper-title">Description</Typography>
                    <div className="object-details-description">
                        {description}
                    </div>
                </Paper>
                <YieldComparisonChart objectData={objectData} tableData={tableData} />
                {returns.length > 0 && (
                    <Paper className="object-details-paper">
                        <Typography variant="h6" className="object-details-paper-title">Performance Metrics</Typography>
                        <div className="object-details-metrics-row">
                            <div>
                                <Typography variant="caption" className="object-details-metrics-label">Total Return</Typography>
                                <Typography variant="body1" className="object-details-metrics-value">
                                    {totalReturn !== null ? `${(totalReturn).toFixed(2)}%` : '—'}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="caption" className="object-details-metrics-label">Annualized Return</Typography>
                                <Typography variant="body1" className="object-details-metrics-value">
                                    {statsAnnReturn !== null ? `${statsAnnReturn}%` : '—'}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="caption" className="object-details-metrics-label">Annualized Volatility</Typography>
                                <Typography variant="body1" className="object-details-metrics-value">
                                    {statsAnnVol !== null ? `${statsAnnVol}%` : '—'}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="caption" className="object-details-metrics-label">Sharpe Ratio</Typography>
                                <Typography variant="body1" className="object-details-metrics-value">
                                    {statsSharpe !== null ? statsSharpe : '—'}
                                </Typography>
                            </div>
                        </div>
                    </Paper>
                )}
                {(returns.length > 0 || returnsLoading || returnsError) && (
                    <Paper className="object-details-paper">
                        <Typography variant="h6" className="object-details-paper-title">Monthly Returns</Typography>
                        {returnsLoading ? (
                            <div className="object-details-spinner"><CircularProgress /></div>
                        ) : returnsError ? (
                            <div className="object-details-error">{returnsError}</div>
                        ) : (
                            <div className="object-details-monthly-table-wrapper">
                                <table className="object-details-monthly-table">
                                    <thead>
                                        <tr>
                                            <th>Year</th>
                                            {RETURN_MONTHS.map((month) => (
                                                <th key={month}>{month}</th>
                                            ))}
                                            <th>Year</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {returnsByYear.map((yearRow) => {
                                            const monthValues = RETURN_MONTHS.map((_, i) => yearRow.months[i]);
                                            const compounded = compoundReturns(monthValues);
                                            return (
                                                <tr key={yearRow.year}>
                                                    <td>{yearRow.year}</td>
                                                    {RETURN_MONTHS.map((_, monthIndex) => {
                                                        const value = yearRow.months[monthIndex];
                                                        return (
                                                            <td key={`${yearRow.year}-${monthIndex}`}>{value === undefined ? '—' : value}</td>
                                                        );
                                                    })}
                                                    <td>{compounded === null ? '—' : `${compounded}%`}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Paper>
                )}
                {returns.length > 0 && !returnsLoading && !returnsError && (
                    <div className="object-details-charts-row">
                        <Paper className="object-details-paper">
                            <Typography variant="h6" className="object-details-paper-title">Compounded Returns</Typography>
                            <div className="object-details-chart-container">
                                <ResponsiveContainer width="100%" height={320}>
                                    <LineChart data={cumulativeChartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                        <XAxis dataKey="month" angle={-30} textAnchor="middle" height={70} interval={0} tickMargin={12} tickFormatter={formatYearEndTick} />
                                        <YAxis tickFormatter={(value) => `${value}%`} />
                                        <Tooltip formatter={(value: any) => (value === null ? '—' : `${value}%`)} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="cumulativeReturn"
                                            name="Cumulative Return"
                                            stroke="#1976d2"
                                            strokeWidth={2}
                                            dot={{ r: 2 }}
                                            connectNulls
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Paper>
                        <Paper className="object-details-paper">
                            <Typography variant="h6" className="object-details-paper-title">Monthly Returns</Typography>
                            <div className="object-details-chart-container">
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart data={monthlyChartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                                        <XAxis dataKey="month" angle={-30} textAnchor="middle" height={70} interval={0} tickMargin={12} tickFormatter={formatYearEndTick} />
                                        <YAxis tickFormatter={(value) => `${value}%`} />
                                        <Tooltip formatter={(value: any) => (value === null ? '—' : `${value}%`)} />
                                        <Legend />
                                        <Bar dataKey="return" name="Monthly Return" fill="#1976d2" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Paper>
                    </div>
                )}
                <Paper className="object-details-paper">
                    <Typography variant="h6" className="object-details-paper-title">Object Data</Typography>
                    {objectLoading ? (
                        <div className="object-details-spinner">
                            <CircularProgress />
                        </div>
                    ) : objectError ? (
                        <div className="object-details-error">{objectError}</div>
                    ) : (
                        <div className="object-details-fields-grid">
                            {visibleFields.map((field) => (
                                <div key={field.key} className="object-details-field-row">
                                    <Typography variant="caption" className="object-details-field-label">
                                        {field.label}
                                    </Typography>
                                    <Typography variant="body2">
                                        {field.key === 'yield_net' || field.key === 'yield_gross' ? (
                                            <span>
                                                {((objectData as any)[field.key]).toFixed(2)}%
                                            </span>
                                        ) : field.key === 'nda' ? (
                                            <span>
                                                {((objectData as any)[field.key] === true || (objectData as any)[field.key] === 1 || String((objectData as any)[field.key]).toLowerCase() === 'true' || String((objectData as any)[field.key]) === '1') ? 'Yes' : 'No'}
                                            </span>
                                        ) : field.key === 'owner' ? (
                                            <span>
                                                {(() => {
                                                    const ownerVal = (objectData as any).owner ?? (objectData as any).company_id ?? (objectData as any).companyId ?? (objectData as any).company ?? null;
                                                    if (ownerVal === null || ownerVal === undefined || String(ownerVal).trim() === '') return '—';
                                                    const name = companyMap.get(String(ownerVal));
                                                    return name ?? String(ownerVal);
                                                })()}
                                            </span>
                                        ) : (
                                            <span>{toDisplay((objectData as any)[field.key])}</span>
                                        )}
                                    </Typography>
                                </div>
                            ))}
                        </div>
                    )}
                </Paper>
            </div>
        </div>
    );
}
